import { useInstantSearch } from 'react-instantsearch';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { FilterTitle } from '@sorare/core/src/components/search/FilterTitle';
import { useFacet } from '@sorare/core/src/contexts/graphql-search';
import type { FilterWidget } from '@sorare/core/src/lib/filters';
import { FILTERS } from '@sorare/core/src/lib/filters';
import { filters } from '@sorare/core/src/lib/glossary';

import {
  BackendRangeSlider,
  RangeSlider,
} from '#src/search/RangeSlider/index.tsx';
import { makeRangeFilter } from '#src/searchCards/RangeSlider/index.tsx';

const AverageScoreTitle = () => {
  const { indexUiState } = useInstantSearch();

  const selected =
    (indexUiState.range?.[FILTERS.lastFiveAverageScore.attribute] ? 1 : 0) +
    (indexUiState.range?.[FILTERS.lastTenPlayedAverageScore.attribute] ? 1 : 0);

  return <FilterTitle name={filters.averageScore} selected={selected} />;
};

const Section = styled(Vertical)`
  &:not(:has(.FilterWidget.visible)) {
    display: none;
  }
`;

const AverageScoreComponent = ({
  fiveAverageScoreWidget,
  tenPlayedAverageScoreWidget,
  visible,
}: {
  fiveAverageScoreWidget: Nullable<FilterWidget>;
  tenPlayedAverageScoreWidget: Nullable<FilterWidget>;
  visible: boolean;
}) => {
  return (
    <Vertical gap={3}>
      {!!fiveAverageScoreWidget && (
        <Section>
          <LabelM color="var(--c-nd-600)">
            <FormattedMessage {...FILTERS.lastFiveAverageScore.title} />
          </LabelM>
          <fiveAverageScoreWidget.Component
            visible={visible}
            {...fiveAverageScoreWidget.props}
          />
        </Section>
      )}
      {!!tenPlayedAverageScoreWidget && (
        <Section>
          <LabelM color="var(--c-nd-600)">
            <FormattedMessage {...FILTERS.lastTenPlayedAverageScore.title} />
          </LabelM>
          <tenPlayedAverageScoreWidget.Component
            visible={visible}
            {...tenPlayedAverageScoreWidget.props}
          />
        </Section>
      )}
    </Vertical>
  );
};

export const RefineAverageScore = (): FilterWidget => {
  const fiveAverageScoreWidget = makeRangeFilter(
    ({ visible }) => (
      <RangeSlider
        attribute={FILTERS.lastFiveAverageScore.attribute}
        visible={visible}
      />
    ),
    FILTERS.lastFiveAverageScore
  );

  const tenPlayedAverageScoreWidget = makeRangeFilter(
    ({ visible }) => (
      <RangeSlider
        attribute={FILTERS.lastTenPlayedAverageScore.attribute}
        visible={visible}
      />
    ),
    FILTERS.lastTenPlayedAverageScore
  );

  return {
    key: FILTERS.averageScore.attribute,
    type: 'range',
    attribute: FILTERS.averageScore.attribute,
    title: <AverageScoreTitle />,
    Component: ({ visible }) => (
      <AverageScoreComponent
        fiveAverageScoreWidget={fiveAverageScoreWidget}
        tenPlayedAverageScoreWidget={tenPlayedAverageScoreWidget}
        visible={visible}
      />
    ),
  };
};

const BackendScoreTitle = () => {
  const { selected: selectedL5 } = useFacet('so5L5Avg');
  const { selected: selectedL10 } = useFacet('so5L10Avg');

  return (
    <FilterTitle
      name={FILTERS.averageScore.title}
      selected={[selectedL5, selectedL10].filter(Boolean).length}
    />
  );
};

export const RefineBackendAverageScore = ({
  disableFiveAverageScore = false,
  disableTenAverageScore = false,
}: {
  disableFiveAverageScore?: boolean;
  disableTenAverageScore?: boolean;
} = {}): FilterWidget => {
  const fiveAverageScoreWidget = {
    key: 'so5L5Avg',
    type: 'range',
    attribute: 'so5L5Avg',
    Component: ({ visible }) => (
      <BackendRangeSlider visible={visible} attribute="so5L5Avg" />
    ),
  } satisfies FilterWidget;

  const tenPlayedAverageScoreWidget = {
    key: 'so5L10Avg',
    type: 'range',
    attribute: 'so5L10Avg',
    Component: ({ visible }) => (
      <BackendRangeSlider visible={visible} attribute="so5L10Avg" />
    ),
  } satisfies FilterWidget;

  return {
    key: FILTERS.averageScore.attribute,
    type: 'range',
    attribute: FILTERS.averageScore.attribute,
    title: <BackendScoreTitle />,
    Component: ({ visible }) => (
      <AverageScoreComponent
        fiveAverageScoreWidget={
          disableFiveAverageScore ? null : fiveAverageScoreWidget
        }
        tenPlayedAverageScoreWidget={
          disableTenAverageScore ? null : tenPlayedAverageScoreWidget
        }
        visible={visible}
      />
    ),
  };
};
